import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, BookOpen, ExternalLink } from 'lucide-react';
import { ENERGY_UNIT_TO_JOULES, FUEL_NCV } from '../../utils/energyUnits';

// Mirrors backend/emission_factors.py (units: ef_* in kg/TJ; ncv in MJ per native unit).
const EMISSION_FACTORS = [
  { fuel: 'Diesel',  category: 'Scope 1 — Liquid',  ncv: 43.0,  unit: 'L',   ef_co2: 74100, ef_ch4: 3,  ef_n2o: 0.6 },
  { fuel: 'Furnace Oil (FO)', category: 'Scope 1 — Liquid', ncv: 40.2, unit: 'L', ef_co2: 77400, ef_ch4: 3, ef_n2o: 0.6 },
  { fuel: 'PNG (Piped Natural Gas)', category: 'Scope 1 — Gas', ncv: 38.0, unit: 'SCM', ef_co2: 56100, ef_ch4: 1, ef_n2o: 0.1 },
  { fuel: 'LPG',     category: 'Scope 1 — Gas',     ncv: 46.1,  unit: 'kg',  ef_co2: 63100, ef_ch4: 1,  ef_n2o: 0.1 },
  { fuel: 'Coal',    category: 'Scope 1 — Solid',   ncv: 18.5,  unit: 'kg',  ef_co2: 94600, ef_ch4: 10, ef_n2o: 1.5 },
  { fuel: 'Biomass', category: 'Scope 1 — Biogenic', ncv: 15.0, unit: 'kg',  ef_co2: 0,     ef_ch4: 5,  ef_n2o: 1.0 },
];

const GRID_FACTORS = [
  { region: 'India',     code: 'INR',     factor: 0.716, source: 'CEA Baseline (Central Electricity Authority)' },
  { region: 'Singapore', code: 'SGD',     factor: 0.408, source: 'EMA (Energy Market Authority)' },
  { region: 'Global default', code: 'default', factor: 0.55, source: 'IFI weighted average' },
];

const GWP_AR6 = [
  { gas: 'CO₂', gwp: 1 },
  { gas: 'CH₄', gwp: 28 },
  { gas: 'N₂O', gwp: 265 },
];

const Section = ({ id, title, children }) => (
  <section id={id} className="bg-white border border-gray-200 rounded-lg p-5 scroll-mt-20">
    <h2 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
      <span className="text-blue-600">§</span> {title}
    </h2>
    <div className="text-sm text-gray-700 space-y-2 leading-relaxed">{children}</div>
  </section>
);

const Cite = ({ children, href }) => (
  href
    ? <a href={href} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-0.5">
        {children}<ExternalLink className="w-3 h-3" />
      </a>
    : <span className="text-gray-600">{children}</span>
);

const Table = ({ children }) => (
  <div className="overflow-x-auto my-2">
    <table className="w-full text-xs border border-gray-200 rounded">{children}</table>
  </div>
);
const Th = ({ children, className = '' }) => (
  <th className={`px-2 py-1.5 bg-gray-50 border-b border-gray-200 text-left font-medium text-gray-700 ${className}`}>{children}</th>
);
const Td = ({ children, className = '' }) => (
  <td className={`px-2 py-1.5 border-b border-gray-100 text-gray-700 ${className}`}>{children}</td>
);

const ERSMethodology = () => {
  const navigate = useNavigate();

  const TOC = [
    { id: 'overview',     label: 'Overview' },
    { id: 'standards',    label: 'Standards & references' },
    { id: 'energy-units', label: 'Energy unit conversions' },
    { id: 'fuel-factors', label: 'Fuel NCV & emission factors' },
    { id: 'grid',         label: 'Grid electricity emission factors' },
    { id: 'renewable',    label: 'Renewable energy treatment' },
    { id: 'thermal',      label: 'Heating / cooling / steam' },
    { id: 'opportunity-engine', label: 'Opportunity Engine V3 — calculation' },
    { id: 'scope3',       label: 'Scope 3 (out of scope here)' },
    { id: 'assumptions',  label: 'Key assumptions & limitations' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-lg" title="Back">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <BookOpen className="w-6 h-6 text-blue-600" />
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Methodology, Standards &amp; Assumptions</h1>
            <p className="text-xs text-gray-500">How the Energy &amp; Resource module calculates energy, emissions, and trends.</p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6 grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* TOC */}
        <aside className="lg:col-span-1">
          <div className="bg-white border border-gray-200 rounded-lg p-4 sticky top-6">
            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Contents</p>
            <ul className="space-y-1 text-sm">
              {TOC.map(t => (
                <li key={t.id}>
                  <a
                    href={`#${t.id}`}
                    className="text-blue-600 hover:underline"
                    onClick={(e) => {
                      // Default anchor click pushes a new history entry, so
                      // browser Back has to undo every TOC tap before leaving
                      // the page. Smooth-scroll and replace state instead.
                      const target = document.getElementById(t.id);
                      if (target) {
                        e.preventDefault();
                        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        window.history.replaceState(null, '', `#${t.id}`);
                      }
                    }}
                  >
                    {t.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        {/* Sections */}
        <div className="lg:col-span-3 space-y-5">
          <Section id="overview" title="Overview">
            <p>
              This page documents the standards, emission factors, conversion bases, and key assumptions
              used by the Energy &amp; Resource (ERS) module — including the data-entry tabs, the inline
              unit converter, the trends/charts page, and the downstream GHG calculations. Numbers here
              reflect what the platform actually uses; if you change them in the codebase (
              <code className="px-1 py-0.5 bg-gray-100 rounded text-xs">backend/emission_factors.py</code> /
              <code className="px-1 py-0.5 bg-gray-100 rounded text-xs">backend/utils/energy_units.py</code>),
              please update this page in lockstep.
            </p>
          </Section>

          <Section id="standards" title="Standards &amp; references">
            <ul className="list-disc list-inside space-y-1">
              <li>
                <b>IPCC AR6</b> — Sixth Assessment Report (2021). Used for Global Warming Potentials (GWP-100)
                and default fuel calorific values / emission factors.{' '}
                <Cite href="https://www.ipcc.ch/report/ar6/wg1/">IPCC AR6 WG1</Cite>
              </li>
              <li>
                <b>GHG Protocol Corporate Standard</b> — Scope 1/2/3 boundary definitions; location-based vs
                market-based Scope 2.{' '}
                <Cite href="https://ghgprotocol.org/corporate-standard">ghgprotocol.org</Cite>
              </li>
              <li>
                <b>CEA Baseline CO₂ Database</b> — Indian grid emission factor (location-based).{' '}
                <Cite href="https://cea.nic.in/cdm-co2-baseline-database/">CEA</Cite>
              </li>
              <li>
                <b>EMA Singapore</b> — Singapore grid emission factor.{' '}
                <Cite href="https://www.ema.gov.sg/">ema.gov.sg</Cite>
              </li>
              <li>
                <b>ISO 31-4 / IUPAC</b> — 1 MMBtu (IT) = 1.05505585262 GJ. Used for the unit converter.
              </li>
              <li>
                <b>SI</b> — 1 kWh = 3.6 MJ exactly (energy = power × time). 1 kcal (thermochemical) = 4.184 kJ.
              </li>
            </ul>
          </Section>

          <Section id="energy-units" title="Energy unit conversions">
            <p>
              All energy values entered on the data tabs can be converted to any of the platform's supported
              energy units via the <span className="font-mono text-xs bg-gray-100 px-1 py-0.5 rounded">ⓘ</span>{' '}
              icon next to a quantity field, and the trends page can normalize the whole study to a target
              unit. Conversions go through joules as a common intermediate to avoid chained rounding error.
            </p>
            <Table>
              <thead>
                <tr><Th>Unit</Th><Th className="text-right">1 unit equals (J)</Th><Th>Notes</Th></tr>
              </thead>
              <tbody>
                {Object.entries(ENERGY_UNIT_TO_JOULES).map(([u, v]) => (
                  <tr key={u}>
                    <Td className="font-mono">{u}</Td>
                    <Td className="text-right font-mono">{v.toExponential(4)}</Td>
                    <Td className="text-gray-500">
                      {u === 'kWh' && 'SI exact (1 kWh = 3.6 MJ)'}
                      {u === 'MMBtu' && 'ISO 31-4 / IUPAC (= 1.0551 GJ)'}
                      {u === 'kcal' && 'Thermochemical (4.184 kJ)'}
                      {u === 'therm' && 'US therm (= 100,000 BTU)'}
                      {u === 'BTU' && 'IT British thermal unit'}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Section>

          <Section id="fuel-factors" title="Fuel NCV &amp; emission factors">
            <p>
              Net calorific values (NCV) and emission factors come from IPCC AR6 defaults. NCV is shown in
              MJ per the fuel's native measurement unit (so 43 MJ/L for diesel, 18.5 MJ/kg for coal, etc).
              Emission factors are in kg of gas per terajoule of energy released (kg/TJ); these are then
              multiplied by GWP-100 to produce CO₂e.
            </p>
            <Table>
              <thead>
                <tr>
                  <Th>Fuel</Th><Th>Category</Th>
                  <Th className="text-right">NCV (MJ/unit)</Th><Th>Unit</Th>
                  <Th className="text-right">CO₂ (kg/TJ)</Th>
                  <Th className="text-right">CH₄ (kg/TJ)</Th>
                  <Th className="text-right">N₂O (kg/TJ)</Th>
                </tr>
              </thead>
              <tbody>
                {EMISSION_FACTORS.map(f => (
                  <tr key={f.fuel}>
                    <Td className="font-medium">{f.fuel}</Td>
                    <Td className="text-gray-500">{f.category}</Td>
                    <Td className="text-right font-mono">{f.ncv}</Td>
                    <Td className="font-mono">{f.unit}</Td>
                    <Td className="text-right font-mono">{f.ef_co2.toLocaleString()}</Td>
                    <Td className="text-right font-mono">{f.ef_ch4}</Td>
                    <Td className="text-right font-mono">{f.ef_n2o}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <p className="text-xs text-gray-500">
              The fuel-quantity → energy converter auto-converts within the same domain — mass↔mass
              (g/kg/MT/ton) and volume↔volume (mL/L/m³/kL) — so 2 MT of Coal is treated as 2,000 kg and
              0.5 m³ of Diesel as 500 L. Cross-domain conversion (kg of diesel, L of LPG) would need a
              density that varies with batch/temperature, so we refuse to guess and the row is excluded.
              SCM is gas-specific (standard cubic metres at STP) and isn&apos;t interchangeable with m³ —
              enter PNG only in SCM.
            </p>
            <Table>
              <thead><tr><Th>Frontend NCV mirror</Th><Th>NCV (MJ/unit)</Th><Th>Native unit</Th></tr></thead>
              <tbody>
                {Object.entries(FUEL_NCV).map(([k, v]) => (
                  <tr key={k}>
                    <Td className="font-mono">{k}</Td>
                    <Td className="font-mono">{v.ncv}</Td>
                    <Td className="font-mono">{v.unit}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Section>

          <Section id="grid" title="Grid electricity emission factors (Scope 2)">
            <p>
              Used for purchased electricity when no supplier-specific market-based factor is available
              (i.e., default to location-based). The grid factor applied is keyed by the tenant&apos;s
              reporting currency / region in <code className="px-1 py-0.5 bg-gray-100 rounded text-xs">GRID_EMISSION_FACTORS</code>.
            </p>
            <Table>
              <thead><tr><Th>Region</Th><Th>Code</Th><Th className="text-right">Factor (kg CO₂e / kWh)</Th><Th>Source</Th></tr></thead>
              <tbody>
                {GRID_FACTORS.map(g => (
                  <tr key={g.code}>
                    <Td>{g.region}</Td>
                    <Td className="font-mono">{g.code}</Td>
                    <Td className="text-right font-mono">{g.factor.toFixed(3)}</Td>
                    <Td className="text-gray-500">{g.source}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Section>

          <Section id="renewable" title="Renewable energy treatment">
            <p>
              Both the Electricity and Fuels data tabs capture a renewable-portion column:
            </p>
            <ul className="list-disc list-inside">
              <li>
                <b>Electricity</b>: <code className="px-1 py-0.5 bg-gray-100 rounded text-xs">renewable_kwh</code>{' '}
                is the portion of the row's total kWh that came from contracted renewable sources (PPA,
                onsite solar/wind, I-REC/RECs). Scope 2 (market-based) deducts this slice from
                grid-attributable emissions.
              </li>
              <li>
                <b>Fuels</b>: <code className="px-1 py-0.5 bg-gray-100 rounded text-xs">renewable_quantity</code>{' '}
                is the portion of the row's total fuel quantity that came from renewable sources
                (biodiesel blended into diesel, biogas into PNG, biomass mixed with coal). Note: pure
                <i> Biomass</i> in the fuel master already has CO₂ factor = 0 (biogenic / carbon neutral
                under IPCC AR6), so don&apos;t double-mark biomass-only rows as renewable.
              </li>
            </ul>
            <p className="text-xs text-gray-500">
              Renewable percentage in trends and aggregates is computed as{' '}
              <span className="font-mono">renewable_quantity / quantity × 100</span>; renewable energy in
              GJ is computed proportionally from <span className="font-mono">total_energy_gj</span>.
            </p>
          </Section>

          <Section id="thermal" title="Heating, cooling &amp; steam">
            <p>
              The Heat / Cool / Steam tab models thermal energy with two source modes per row:
            </p>
            <ul className="list-disc list-inside space-y-1">
              <li>
                <b>Purchased</b> — district heating, district cooling, or purchased steam from a utility.
                Counts toward Scope 2 emissions; the row carries a supplier-specific emission factor
                (kg CO₂e per unit) when known, or falls back to a platform default.
              </li>
              <li>
                <b>Self-generated</b> — thermal output of an on-site boiler, chiller, or CHP unit.
                The thermal output is captured for energy-balance reporting only; the upstream fuel
                or electricity that drove it is already accounted for in the Fuels / Electricity tabs,
                so calculations <b>do not count it twice</b>. The optional source-fuel reference and
                efficiency % are kept for documentation and energy-chain analysis.
              </li>
            </ul>
            <p>
              <b>Native units (ton-h, kg):</b> refrigeration ton-hours and kg of steam can&apos;t be
              converted to GJ/kWh without enthalpy data (steam pressure/temperature) or a chiller&apos;s
              refrigeration rating. The trends page surfaces these rows in a separate panel rather than
              normalizing them and risking misleading totals.
            </p>
          </Section>

          <Section id="opportunity-engine" title="Opportunity Engine V3 — calculation methodology">
            <p>
              For each Significant Energy User (SEU) the engine generates a ranked list of energy-saving
              interventions with screening-grade CAPEX, annual ₹ savings, annual tCO₂e avoided, payback,
              and ROI. Numbers are starting points for vendor quotation and engineering sign-off — not a
              substitute for M&amp;V (IPMVP Options A–D).
            </p>

            <p className="font-semibold text-gray-800 mt-3">Inputs</p>
            <ul className="list-disc list-inside text-sm">
              <li><b>Site SEU register</b> — equipment, system, group, rated kW (or kVA / TR / lump-sum), hours/day, load %, derived annual energy (kWh / GJ / KL / MT).</li>
              <li><b>Vetted CAPEX-item master library</b> — opportunities filtered by tenant industry. Each row carries <code>unit_cost_low</code>, <code>unit_cost_high</code>, <code>capacity_basis</code>, <code>lifecycle_years</code>, default <code>savings %</code>.</li>
            </ul>

            <p className="font-semibold text-gray-800 mt-3">5-stage pipeline (per SEU × opportunity)</p>

            <p><b>1. Matching.</b> Each SEU is scored against every opportunity in the foundry/sector-filtered master; top 5 (score ≥ 25) are kept. Signals:</p>
            <Table>
              <thead><tr><Th>Signal</Th><Th className="text-right">Points</Th></tr></thead>
              <tbody>
                <tr><Td>Same SEU category letter (A. Electrical, B. Thermal, etc.)</Td><Td className="text-right font-mono">+20</Td></tr>
                <tr><Td>Equipment-keyword hit in opp text (1 / 2 / 3+ hits)</Td><Td className="text-right font-mono">+20 / +35 / +50</Td></tr>
                <tr><Td>Equipment name appears directly in opp item name</Td><Td className="text-right font-mono">+15</Td></tr>
                <tr><Td>System / group word in opp text</Td><Td className="text-right font-mono">+10</Td></tr>
              </tbody>
            </Table>
            <p className="text-xs text-gray-500">A resource-type gate also runs first (e.g. water SEUs can&apos;t match thermal opps) to prevent cross-resource nonsense.</p>

            <p className="mt-2"><b>2. CAPEX sizing.</b></p>
            <pre className="text-xs bg-gray-50 border border-gray-200 rounded p-2 overflow-x-auto">
{`If capacity_basis ∈ {kW, kVA, TR}:
    CAPEX_low  = unit_cost_low  × SEU_capacity
    CAPEX_high = unit_cost_high × SEU_capacity
Else (lump-sum, e.g. PFC bank):
    CAPEX_low  = unit_cost_low
    CAPEX_high = unit_cost_high

Avg_CAPEX = (CAPEX_low + CAPEX_high) / 2`}
            </pre>

            <p className="mt-2"><b>3. Annual savings &amp; emissions.</b></p>
            <pre className="text-xs bg-gray-50 border border-gray-200 rounded p-2 overflow-x-auto">
{`Annual_Energy_Cost  = annual_energy × resource_rate                  (₹/yr)
Annual_Savings_INR  = Annual_Energy_Cost × savings_%                 (₹/yr)
Annual_Energy_Saved = annual_energy × savings_%                      (native unit)
Annual_tCO2e        = Annual_Energy_Saved × emission_factor / 1000   (tCO2e/yr)`}
            </pre>

            <p className="mt-2"><b>4. Payback, ROI, viability.</b></p>
            <pre className="text-xs bg-gray-50 border border-gray-200 rounded p-2 overflow-x-auto">
{`Simple_Payback (yrs) = Avg_CAPEX / Annual_Savings_INR
ROI (%)              = (Annual_Savings × lifecycle_yrs − Avg_CAPEX) / Avg_CAPEX × 100
Viable               = Payback < 15 yrs AND ROI > 0`}
            </pre>
            <p className="text-xs text-gray-500">Lifecycle defaults to 10 years and is overridden per item where the master defines one (motors ~ 15, VFDs 10, heat exchangers 20, LEDs 7-10).</p>

            <p className="mt-2"><b>5. CAPEX tier classification</b> (for the dashboard buckets):</p>
            <Table>
              <thead><tr><Th>Tier</Th><Th className="text-right">Avg CAPEX</Th></tr></thead>
              <tbody>
                <tr><Td>No CAPEX</Td><Td className="text-right font-mono">≤ ₹10,000</Td></tr>
                <tr><Td>Low CAPEX</Td><Td className="text-right font-mono">₹10,000 – ₹5,00,000</Td></tr>
                <tr><Td>Medium CAPEX</Td><Td className="text-right font-mono">₹5,00,000 – ₹25,00,000</Td></tr>
                <tr><Td>High CAPEX</Td><Td className="text-right font-mono">&gt; ₹25,00,000</Td></tr>
              </tbody>
            </Table>

            <p className="font-semibold text-gray-800 mt-3">Default resource rates (India; editable per study via tariff overrides)</p>
            <Table>
              <thead><tr><Th>Resource</Th><Th className="text-right">Rate</Th><Th>Basis</Th></tr></thead>
              <tbody>
                <tr><Td>Electricity</Td><Td className="text-right font-mono">₹8.00 / kWh</Td><Td>Industrial India average</Td></tr>
                <tr><Td>PNG / Natural Gas</Td><Td className="text-right font-mono">₹1,450 / GJ</Td><Td>≈ ₹55 / SCM</Td></tr>
                <tr><Td>LPG</Td><Td className="text-right font-mono">₹1,780 / GJ</Td><Td>Industrial bulk</Td></tr>
                <tr><Td>Diesel / HSD</Td><Td className="text-right font-mono">₹2,400 / GJ</Td><Td>Industrial</Td></tr>
                <tr><Td>Furnace Oil</Td><Td className="text-right font-mono">₹1,400 / GJ</Td><Td>Industrial</Td></tr>
                <tr><Td>Coal</Td><Td className="text-right font-mono">₹500 / GJ</Td><Td>India average</Td></tr>
                <tr><Td>Water</Td><Td className="text-right font-mono">₹30 / KL</Td><Td>Municipal + borewell blend</Td></tr>
                <tr><Td>Waste disposal</Td><Td className="text-right font-mono">₹2,000 / MT</Td><Td>Avoided-cost proxy</Td></tr>
              </tbody>
            </Table>

            <p className="font-semibold text-gray-800 mt-3">Emission factors used (see also §Grid &amp; §Fuel-NCV above)</p>
            <p className="text-sm">Grid electricity 0.716 kg CO₂e/kWh (CEA India). Combustion fuels: PNG 56.1, LPG 63.1, Diesel/HSD 74.1, FO 77.4, Coal 94.6, Biomass 0 (biogenic, reported separately). All values kg CO₂e per GJ — IPCC 2006 Tier 1 + BEE PAT defaults.</p>

            <p className="font-semibold text-gray-800 mt-3">Disclaimers (audit-defensible language)</p>
            <ul className="list-disc list-inside text-sm">
              <li>Outputs are <b>screening-grade estimates</b> for portfolio prioritisation, not engineering deliverables.</li>
              <li>Final CAPEX requires <b>vendor quotation</b>; final savings require <b>M&amp;V per IPMVP</b> Options A–D.</li>
              <li>Savings % defaults are conservative — site-specific override recommended before commitment.</li>
              <li>Payback &lt; 1 yr typically indicates housekeeping / leak-fix items (negligible CAPEX). Payback &gt; 7 yr indicates strategic capital projects — additional financial and operational analysis recommended.</li>
              <li>The AI Report on the Portfolio screen is a narrative summary of the same numbers; it does not recompute anything.</li>
            </ul>
          </Section>

          <Section id="scope3" title="Scope 3 (handled elsewhere)">
            <p>
              The ERS module covers Scope 1 (direct fuel combustion + fugitive on-site) and Scope 2
              (purchased electricity and purchased heat/cool/steam). Scope 3 (upstream &amp; downstream
              value-chain emissions) is handled in the separate Scope 3 module of the platform and is
              not aggregated into the trends / GHG summary here unless explicitly imported.
            </p>
          </Section>

          <Section id="assumptions" title="Key assumptions &amp; limitations">
            <ul className="list-disc list-inside space-y-1">
              <li>Fuel NCV and emission factors are IPCC defaults; if you have measured calorific values
              for your fuel, override <code className="px-1 py-0.5 bg-gray-100 rounded text-xs">calorific_value</code>{' '}
              on the row.</li>
              <li>Grid emission factors are location-based unless a supplier-specific factor is provided.</li>
              <li>kcal here is the <i>thermochemical</i> kcal (4.184 kJ); the older 15 °C kcal (4.1855 kJ)
              and IT kcal (4.1868 kJ) are not used.</li>
              <li>BTU is the IT British thermal unit (1055.05585262 J). MMBtu = 10⁶ BTU.</li>
              <li>Renewable electricity / fuel claims should be backed by contractual instruments
              (PPA, RECs, I-RECs, sustainability certificates). The platform does not verify these —
              the entered values are taken at face value.</li>
              <li>Trends totals exclude thermal rows in non-energy units (ton-h, kg) by design, to
              avoid misleading sums.</li>
              <li>All data here is tenant-isolated (schema-per-tenant). Cross-tenant aggregation is
              not exposed.</li>
            </ul>
          </Section>

          <p className="text-xs text-gray-400 italic text-center py-4">
            Last reviewed against codebase on the date you see in your top-right; if you change emission
            factors or unit constants, update this page too.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ERSMethodology;
